// src/utils/productInventoryModel.js

// 📦 Product Inventory Schema
export const ProductInventorySchema = {
  _id: '',
  branch: '', // Branch ID or populated branch object
  product: '', // Product ID or populated product object
  currentStock: 0,
  lastUpdated: null,
  createdAt: null,
  updatedAt: null,
  createdBy: null
}

// 🛡️ Validation Rules
export const PRODUCT_INVENTORY_VALIDATION_RULES = {
  branch: {
    required: true,
    message: 'Branch is required'
  },
  product: {
    required: true,
    message: 'Product is required'
  },
  currentStock: {
    required: true,
    min: 0,
    max: 999999,
    message: 'Current stock must be a positive number between 0 and 999999'
  }
}

// 📊 Stock Status Enums
export const PRODUCT_STOCK_STATUS = {
  NORMAL: 'normal',
  LOW_STOCK: 'low_stock',
  OUT_OF_STOCK: 'out_of_stock',
  OVER_STOCK: 'over_stock'
}

// 🎨 Status Display Labels
export const PRODUCT_STOCK_STATUS_LABELS = {
  [PRODUCT_STOCK_STATUS.NORMAL]: 'Normal',
  [PRODUCT_STOCK_STATUS.LOW_STOCK]: 'Low Stock',
  [PRODUCT_STOCK_STATUS.OUT_OF_STOCK]: 'Out of Stock',
  [PRODUCT_STOCK_STATUS.OVER_STOCK]: 'Over Stock'
}

// 🎨 Status Colors
export const PRODUCT_STOCK_STATUS_COLORS = {
  [PRODUCT_STOCK_STATUS.NORMAL]: 'bg-blue-100 text-blue-800',
  [PRODUCT_STOCK_STATUS.LOW_STOCK]: 'bg-yellow-100 text-yellow-800',
  [PRODUCT_STOCK_STATUS.OUT_OF_STOCK]: 'bg-red-100 text-red-800',
  [PRODUCT_STOCK_STATUS.OVER_STOCK]: 'bg-green-100 text-green-800'
}

// 🏭 Product Inventory Model Class
export class ProductInventoryModel {
  
  // Create a new inventory instance
  static create(data = {}) {
    return {
      ...ProductInventorySchema,
      ...data,
      _id: data._id || `temp_${Date.now()}`,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString()
    }
  }

  // Validate full inventory data
  static validate(data) {
    const errors = {}
    let isValid = true

    // Validate branch
    if (!data.branch || (typeof data.branch === 'string' && !data.branch.trim())) {
      errors.branch = PRODUCT_INVENTORY_VALIDATION_RULES.branch.message
      isValid = false
    }

    // Validate product
    if (!data.product || (typeof data.product === 'string' && !data.product.trim())) {
      errors.product = PRODUCT_INVENTORY_VALIDATION_RULES.product.message
      isValid = false
    }

    // Validate current stock
    const stock = Number(data.currentStock)
    if (isNaN(stock) || stock < 0 || stock > 999999) {
      errors.currentStock = PRODUCT_INVENTORY_VALIDATION_RULES.currentStock.message
      isValid = false
    }

    return { isValid, errors }
  }

  // Validate partial data (for updates)
  static validatePartial(data) {
    const errors = {}
    let isValid = true

    // Only validate fields that are present
    if (data.hasOwnProperty('currentStock')) {
      const stock = Number(data.currentStock)
      if (isNaN(stock) || stock < 0 || stock > 999999) {
        errors.currentStock = PRODUCT_INVENTORY_VALIDATION_RULES.currentStock.message
        isValid = false
      }
    }

    return { isValid, errors }
  }

  // Get stock status based on current stock and threshold
  static getStockStatus(currentStock, threshold = 10) {
    const stock = Number(currentStock) || 0
    const thresholdValue = Number(threshold) || 10

    if (stock === 0) {
      return PRODUCT_STOCK_STATUS.OUT_OF_STOCK
    } else if (stock <= thresholdValue) {
      return PRODUCT_STOCK_STATUS.LOW_STOCK
    } else if (stock > thresholdValue * 3) {
      return PRODUCT_STOCK_STATUS.OVER_STOCK
    } else {
      return PRODUCT_STOCK_STATUS.NORMAL
    }
  }

  // Get status display label
  static getStatusLabel(status) {
    return PRODUCT_STOCK_STATUS_LABELS[status] || 'Unknown'
  }

  // Get status color classes
  static getStatusColor(status) {
    return PRODUCT_STOCK_STATUS_COLORS[status] || PRODUCT_STOCK_STATUS_COLORS[PRODUCT_STOCK_STATUS.NORMAL]
  }

  // Format inventory data for display
  static formatForDisplay(inventory) {
    if (!inventory) return null

    const currentStock = Number(inventory.currentStock) || 0
    const threshold = Number(inventory.product?.threshold) || 10
    const status = this.getStockStatus(currentStock, threshold)

    return {
      ...inventory,
      currentStock,
      threshold,
      status,
      statusLabel: this.getStatusLabel(status),
      statusColor: this.getStatusColor(status),
      productName: inventory.product?.name || 'Unknown Product',
      productSku: inventory.product?.sku || 'N/A',
      branchName: inventory.branch?.name || 'Unknown Branch',
      unitName: inventory.product?.unit?.name || 'Unit',
      unitAbbreviation: inventory.product?.unit?.abbreviation || 'U',
      categoryName: inventory.product?.category?.name || 'Uncategorized',
      outletPrice: inventory.product?.outletPrice || 0,
      pickMePrice: inventory.product?.pickMePrice || 0,
      uberPrice: inventory.product?.uberPrice || 0,
      cost: inventory.product?.cost || 0
    }
  }

  // Prepare data for API create request
  static prepareForCreate(formData) {
    return {
      branch: formData.branch,
      product: formData.product,
      currentStock: Number(formData.currentStock) || 0
    }
  }

  // Prepare data for API update request
  static prepareForUpdate(formData, originalData = {}) {
    const updateData = {}

    // Only include fields that have changed
    if (formData.hasOwnProperty('currentStock') && 
        Number(formData.currentStock) !== Number(originalData.currentStock)) {
      updateData.currentStock = Number(formData.currentStock)
    }

    return updateData
  }

  // Check if inventory data is valid for display
  static isValidForDisplay(inventory) {
    return inventory && 
           inventory._id && 
           inventory.product && 
           inventory.branch &&
           typeof inventory.currentStock !== 'undefined'
  }

  // Get formatted stock display with unit
  static getStockDisplay(inventory) {
    if (!inventory) return '0'
    
    const stock = Number(inventory.currentStock) || 0
    const unit = inventory.product?.unit?.abbreviation || 'units'
    
    return `${stock.toLocaleString()} ${unit}`
  }

  // Get price display
  static getPriceDisplay(price, currency = 'Rs') {
    const numericPrice = Number(price) || 0
    return `${currency} ${numericPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  // Compare inventories for sorting
  static compare(a, b, sortBy = 'product.name', order = 'asc') {
    let valueA, valueB

    // Handle nested properties
    if (sortBy.includes('.')) {
      const keys = sortBy.split('.')
      valueA = keys.reduce((obj, key) => obj?.[key], a)
      valueB = keys.reduce((obj, key) => obj?.[key], b)
    } else {
      valueA = a[sortBy]
      valueB = b[sortBy]
    }

    // Handle string comparison
    if (typeof valueA === 'string') {
      valueA = valueA.toLowerCase()
      valueB = valueB?.toLowerCase() || ''
    }

    // Handle null/undefined values
    if (valueA == null) valueA = ''
    if (valueB == null) valueB = ''

    if (order === 'asc') {
      return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
    } else {
      return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
    }
  }
}

export default ProductInventoryModel